/**
 * Zaifee — shared theme runtime.
 *
 * Everything here is framework-free and safe to load with `defer`. It provides
 * the pieces individual sections need: localized cart calls, Shopify-correct
 * money formatting, a section-lifecycle helper that survives the theme editor,
 * and small accessibility utilities.
 */
(function () {
  'use strict';

  var routes = window.themeRoutes || {};
  var strings = window.themeStrings || {};
  var themeSettings = window.themeSettings || {};

  /* ------------------------------------------------------------------ money */

  /**
   * Formats an amount in cents using the shop's own money format string.
   * Handles all four Shopify placeholders rather than only the first match.
   */
  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  /**
   * Returns a usable money format, repairing one an app has mangled.
   *
   * Currency apps rewrite Settings > General > Currency formatting so they can
   * find prices, and they do it by string surgery on whatever is already
   * there. That goes wrong on a custom format: wrapping "Rs.{{amount}}" has
   * been seen to produce
   *
   *     Rs.{{amoun<span class=money>Rs.{{amount}}</span>t}}
   *
   * which prints as "Rs.{{amounRs.4,299.00t}}" on the storefront.
   *
   * A healthy format holds exactly one {{placeholder}} and no other braces.
   * When that does not hold, prefer a well-formed fragment wrapped in a single
   * element - that is the format the app was trying to install, so keeping it
   * leaves the app working - and fall back to the leading currency text plus
   * the placeholder.
   *
   * This can only correct the prices rendered here. Prices rendered by Liquid
   * go through Shopify's own money filter, which no theme can intercept, so a
   * mangled setting still has to be repaired in the admin.
   */
  function repairMoneyFormat(raw) {
    if (!raw) return '${{amount}}';

    var leftovers = raw.replace(PLACEHOLDER, '');
    if (leftovers.indexOf('{{') === -1 && leftovers.indexOf('}}') === -1) return raw;

    var wrapped = raw.match(/<\w+[^>]*>[^<{}]*\{\{\s*\w+\s*\}\}[^<{}]*<\/\w+>/);
    if (wrapped) return wrapped[0];

    var placeholder = raw.match(PLACEHOLDER);
    var brace = raw.indexOf('{{');
    var prefix = (brace > -1 ? raw.slice(0, brace) : '').replace(/<[^>]*>/g, '');
    return prefix + (placeholder ? placeholder[0] : '{{amount}}');
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    var value = '';
    var placeholderRegex = PLACEHOLDER;
    var formatString = repairMoneyFormat(format || themeSettings.moneyFormat);

    function defaultTo(number, size) {
      var n = Math.abs(parseFloat(number) || 0).toFixed(size);
      return n;
    }

    function withDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) return '0';

      var fixed = defaultTo(number / 100.0, precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    switch (match && match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------- cart */

  function cartRequest(url, body) {
    var options = {
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json' }
    };

    if (body instanceof FormData) {
      options.body = body;
    } else if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) {
          var error = new Error(data.description || data.message || strings.cartError);
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  /**
   * Reflects cart state into every count badge on the page.
   *
   * Individual scripts used to each carry their own list of badge selectors,
   * which drifted: the cart drawer targeted #desktop-cart-count and
   * #mobile-cart-count, neither of which exists in the markup, so changes made
   * in the drawer never reached the header. Everything routes through here now.
   */
  function syncCartCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    document.querySelectorAll('[data-cart-count], #site-cart-count').forEach(function (el) {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
    });
  }

  var Cart = {
    get: function () {
      return cartRequest(routes.cart + '.js');
    },
    add: function (body) {
      return cartRequest(routes.cartAdd, body);
    },
    change: function (body) {
      return cartRequest(routes.cartChange, body);
    },
    update: function (body) {
      return cartRequest(routes.cartUpdate, body);
    },
    /** Re-renders a section server-side so money and discounts stay correct. */
    section: function (sectionId) {
      return fetch(routes.root + '?section_id=' + encodeURIComponent(sectionId)).then(function (r) {
        return r.text();
      });
    },
    publish: function (cart) {
      syncCartCount(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    }
  };

  /* Cart updates are dispatched on `document` by some callers and on `window`
     by others. Sync from both, and forward document -> window once so listeners
     bound to either target see every update. The forwarded flag stops the two
     handlers bouncing the event back and forth. */
  document.addEventListener('cart:updated', function (event) {
    var detail = event.detail || {};
    syncCartCount(detail.cart);
    if (detail.__forwarded) return;
    window.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: detail.cart, __forwarded: true }
      })
    );
  });

  window.addEventListener('cart:updated', function (event) {
    syncCartCount((event.detail || {}).cart);
  });

  /* -------------------------------------------------------------- lifecycle */

  var registry = [];

  /**
   * Registers section behaviour so it runs on first paint and again whenever
   * the theme editor re-renders that section.
   *
   * @param {string} selector root element selector for the section
   * @param {function(HTMLElement): (function()|void)} setup returns an optional teardown
   */
  function registerSection(selector, setup) {
    var entry = { selector: selector, setup: setup, teardowns: new WeakMap() };
    registry.push(entry);
    mount(entry, document);
  }

  function mount(entry, scope) {
    scope.querySelectorAll(entry.selector).forEach(function (el) {
      if (entry.teardowns.has(el)) return;
      var teardown = entry.setup(el);
      entry.teardowns.set(el, typeof teardown === 'function' ? teardown : function () {});
    });
  }

  function unmount(entry, scope) {
    scope.querySelectorAll(entry.selector).forEach(function (el) {
      var teardown = entry.teardowns.get(el);
      if (teardown) {
        teardown();
        entry.teardowns.delete(el);
      }
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    registry.forEach(function (entry) {
      mount(entry, event.target);
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    registry.forEach(function (entry) {
      unmount(entry, event.target);
    });
  });

  /* ---------------------------------------------------------- accessibility */

  function announce(message) {
    var region = document.getElementById('cart-live-region');
    if (region) region.textContent = message;
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** Traps focus inside a drawer or modal and restores it on release. */
  function trapFocus(container) {
    var previous = document.activeElement;
    var nodes = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE));
    if (!nodes.length) return function () {};

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    nodes[0].focus();

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (previous && previous.focus) previous.focus();
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------ action delegation */

  /**
   * Delegated handler for `data-action` attributes.
   *
   * The markup used to carry ~57 inline `onclick` attributes. Those only work
   * because every handler is a global, they cannot be bound to markup rendered
   * later, and they are the first thing to break under a Content Security
   * Policy — which merchants do get from apps and proxies. One listener on the
   * document replaces all of them and keeps working for markup injected after
   * load, which is most of this theme's cart and drawer UI.
   *
   * Markup contract:
   *
   *   data-action="closeCartDrawer"                  -> closeCartDrawer()
   *   data-action="slidePdpGallery" data-args="[-1]" -> slidePdpGallery(-1)
   *   data-args='["$el", "123"]'                     -> fn(theElement, '123')
   *   data-args='["$event"]'                         -> fn(theEvent)
   *   data-args='["$value"]'                         -> fn(element.value)
   *   data-stop                                      -> stopPropagation + preventDefault
   *   data-action-self                               -> only fire when the click
   *                                                     landed on the element
   *                                                     itself (backdrops)
   */
  function runAction(element, event) {
    var name = element.getAttribute('data-action');
    var fn = window[name];
    if (typeof fn !== 'function') return;

    if (element.hasAttribute('data-stop')) {
      event.stopPropagation();
      event.preventDefault();
    }

    var args = [];
    var raw = element.getAttribute('data-args');
    if (raw) {
      try {
        args = JSON.parse(raw);
      } catch (error) {
        args = [raw];
      }
      if (!Array.isArray(args)) args = [args];
      args = args.map(function (value) {
        if (value === '$el') return element;
        if (value === '$event') return event;
        if (value === '$value') return element.value;
        return value;
      });
    }

    fn.apply(element, args);
  }

  function delegateActions(type) {
    document.addEventListener(type, function (event) {
      var element = event.target.closest && event.target.closest('[data-action]');
      if (!element) return;
      if (element.getAttribute('data-action-on') !== type) {
        // default is click; anything else opts in explicitly
        if (type !== 'click' || element.hasAttribute('data-action-on')) return;
      }
      // Backdrops want the click that landed on them, not on their contents.
      if (element.hasAttribute('data-action-self') && event.target !== element) return;
      runAction(element, event);
    });
  }

  delegateActions('click');
  delegateActions('change');
  delegateActions('input');

  /* ------------------------------------------------- lazily played background video */

  /**
   * Plays background video only when it is both on screen and actually
   * displayed.
   *
   * Every hero slide renders a desktop and a mobile file, and CSS
   * `display: none` does not stop a video downloading -- with `autoplay` the
   * browser fetched all of them, on-screen or not. Six files, ~19 MB, before
   * anything was visible. `preload="none"` in the markup plus this observer
   * means a file is fetched when it is about to be seen, and never otherwise.
   */
  function heroVideoVisible(video) {
    // offsetParent is null whenever an ancestor is display:none, which is how
    // the desktop/mobile variants are hidden from each other.
    return video.offsetParent !== null && video.dataset.autoplay !== 'false';
  }

  function watchHeroVideos(scope) {
    // Scoped to video elements on purpose. .hero-media-element is on the hero's
    // image as well as its video, so a bare class selector hands this observer
    // <img> elements and video.play() throws on them - which it did, twice per
    // page load, on any hero using an image.
    var videos = (scope || document).querySelectorAll(
      'video.hero-media-element, video.split-banner-video'
    );
    if (!videos.length) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      return; // poster frame is a perfectly good fallback
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting && heroVideoVisible(video)) {
            var playing = video.play();
            if (playing && playing.catch) playing.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px' }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  watchHeroVideos(document);
  document.addEventListener('shopify:section:load', function (event) {
    watchHeroVideos(event.target);
  });

  /* --------------------------------------------------------- product cards */

  /**
   * Whole-card navigation.
   *
   * The card is a link target with real controls inside it (wishlist, quick
   * add, the image link). Anything genuinely interactive handles its own click,
   * so this only fires for clicks on the card's own surface.
   */
  function cardTarget(event) {
    var card = event.target.closest && event.target.closest('.product-card-item');
    if (!card || !card.dataset.productUrl) return null;
    if (event.target.closest('a, button, [data-action], input, select, label')) return null;
    return card.dataset.productUrl;
  }

  document.addEventListener('click', function (event) {
    var url = cardTarget(event);
    if (url) window.location.href = url;
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var url = cardTarget(event);
    if (url) {
      event.preventDefault();
      window.location.href = url;
    }
  });


  /** Card videos are preload="none", so they only fetch once hovered. */
  document.addEventListener('mouseenter', function (event) {
    var card = event.target.closest && event.target.closest('.product-card-item');
    if (!card) return;
    var video = card.querySelector('.product-card-video');
    if (video && video.paused) {
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    }
  }, true);

  document.addEventListener('mouseleave', function (event) {
    var card = event.target.closest && event.target.closest('.product-card-item');
    if (!card) return;
    var video = card.querySelector('.product-card-video');
    if (video && !video.paused) {
      video.pause();
      video.currentTime = 0;
    }
  }, true);

  /* ----------------------------------------------------------------- export */

  // Only a store whose money format has been mangled by a currency app needs
  // the pass that repairs prices Liquid already rendered, so it is a separate
  // file fetched on demand rather than weight every shopper downloads.
  (function loadMoneyNormaliser() {
    var raw = themeSettings.moneyFormat;
    if (!raw || repairMoneyFormat(raw) === raw) return;
    if (!themeSettings.moneyNormaliserUrl) return;

    var script = document.createElement('script');
    script.src = themeSettings.moneyNormaliserUrl;
    script.defer = true;
    document.head.appendChild(script);
  })();

  window.theme = {
    routes: routes,
    strings: strings,
    settings: themeSettings,
    formatMoney: formatMoney,
    repairMoneyFormat: repairMoneyFormat,
    Cart: Cart,
    registerSection: registerSection,
    announce: announce,
    trapFocus: trapFocus,
    prefersReducedMotion: prefersReducedMotion
  };
})();
